/**
 * Loads QuaggaJS on demand, once.
 *
 * The bundle is ~93KB and is only needed when a customer actually opens the barcode
 * scanner, so it is no longer loaded in the document head. It is a UMD bundle (it
 * assigns `window.Quagga`), not an ES module, so it is injected as a classic script
 * rather than imported.
 *
 * The in-flight promise is cached, so repeated opens - and concurrent opens - reuse the
 * single load instead of injecting another script tag. A tag that is already in the
 * document (left in the layout, or injected by an earlier call) is reused rather than
 * duplicated. On failure the cache is cleared so a later attempt can retry.
 *
 * @param {string} [url] - Asset URL of the Quagga bundle. Defaults to `theme.scripts.quagga`.
 * @returns {Promise<object>} Resolves with `window.Quagga`.
 */
let quaggaLoader = null;
function loadQuagga(url) {
    // Already loaded - by an earlier open, or by anything else on the page.
    if (window.Quagga) return Promise.resolve(window.Quagga);

    // A load is already in flight or complete - reuse it.
    if (quaggaLoader) return quaggaLoader;

    const src = url || (window.theme && window.theme.scripts && window.theme.scripts.quagga);

    if (!src) {
        return Promise.reject(new Error('No Quagga asset URL provided.'));
    }

    quaggaLoader = new Promise((resolve, reject) => {
        // The script may already be in the document but not finished executing. Attach to
        // that tag instead of appending a second copy of a 93KB bundle.
        let script = document.querySelector('script[src*="quagga"]');
        const isNewTag = !script;

        const onLoad = () => {
            if (window.Quagga) {
                resolve(window.Quagga);
            } else {
                quaggaLoader = null;
                if (isNewTag) script.remove();
                reject(new Error('Quagga loaded but did not register on window.'));
            }
        };

        const onError = () => {
            // Allow a retry on the next open.
            quaggaLoader = null;
            if (isNewTag) script.remove();
            reject(new Error(`Failed to load Quagga from ${src}`));
        };

        if (isNewTag) {
            script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.dataset.quaggaLoader = '';
            script.addEventListener('load', onLoad);
            script.addEventListener('error', onError);
            document.head.appendChild(script);
        } else {
            script.addEventListener('load', onLoad);
            script.addEventListener('error', onError);
        }
    });

    return quaggaLoader;
}

if (!customElements.get('barcode-search')) {

    class BarcodeSearch extends HTMLElement {
        constructor() {
            super();

            this._code = null;

            this.token = this.dataset.token;
            this.container = this.querySelector('#barCodeScanner');
            this.loadingContainer = this.querySelector('[data-loading]');
            this.loadingResultContainer = this.querySelector('[data-loading-result]');
            this.resultContainer = this.querySelector('[data-barcode-result]');
            this.cacheContainer = this.querySelector('[data-barcode-result-cached]');
            this.deviceSelectionContainer = this.querySelector('[data-device-selection-container]');
            this.notFoundContainer = this.querySelector('[data-not-found]');
            this.videoDevices = null;
            this.tempResults = JSON.parse(localStorage.getItem('barcode_cached_results')) || [];

            this.online = true;
            this.products = [];

            this.config = {
                inputStream: {
                    type: "LiveStream",
                    target: this.container,
                    constraints: {
                        facingMode: "environment",
                    }
                },
                debug: {
                    drawBoundingBox: false,
                    showFrequency: false,
                    drawScanline: false,
                    showPattern: false
                },
                locator: {
                    patchSize: "medium",
                    halfSample: !0
                },
                numOfWorkers: 0,
                frequency: 10,
                decoder: {
                    readers: [
                        {
                            format: "code_128_reader",
                            config: {}
                        }, {
                            format: "ean_reader",
                            config: {}
                        }, {
                            format: "ean_8_reader",
                            config: {}
                        }
                    ]
                },
                locate: !0
            };

            this.cacheContainer.innerHTML = `
                <div class="main-products-grid main-products-grid--contained flex" data-layout="list">
                    <div
                        class="main-products-grid__results relative flex-auto"
                        id="psearch-product"
                        role="tabpanel"
                        tabindex="0"
                        aria-labelledby="psearch-product-tab"
                    >
                        <div class="promo-item-scan-title">Vorherige Scanergebnisse:</div>
                        <ul class="grid gapless mb-8 md:mb-12 grid-cols-2 small-cards-mobile sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 small-cards-desktop gap-card" role="list">
                            <li class="promo-item--grid_list js-pagination-result" role="option">${this.tempResults.join('')}</li>
                        </ul>
                    </div>
                </div>`;
        }

        set code(value) {
            this._code = value;
            if (value != null) this.search();
        }

        get code() {
            return this._code;
        }

        disconnectedCallback() {
            // Quagga may never have loaded - the drawer can be closed while the bundle is
            // still downloading, or after a failed load.
            if (window.Quagga) Quagga.stop();
            document.removeEventListener('keydown', this.escapeEventListener.bind(this));
            this.deviceSelectionContainer.querySelector('select').removeEventListener('change', this.handleChangeDevice.bind(this))

            window.removeEventListener('online', this.handleGoOnline.bind(this));
            window.removeEventListener('offline', this.handleGoOffline.bind(this));
        }

        async connectedCallback() {
            const offlineSearchTimestamp = new Date(localStorage.getItem('barcode_offline_timestamp'));
            const currentDate = new Date();
            if (currentDate > offlineSearchTimestamp) {
                await this.preload();
            } else {
                this.products = JSON.parse(localStorage.getItem('barcode_offline_products'));
            }

            // this.online = false;

            this.deviceSelectionContainer.querySelector('select').addEventListener('change', this.handleChangeDevice.bind(this));

            window.addEventListener('online', this.handleGoOnline.bind(this));
            window.addEventListener('offline', this.handleGoOffline.bind(this));

            // Fetched on the first scan only; later scans reuse the cached load.
            try {
                await loadQuagga();
            } catch (error) {
                console.error('[BarcodeSearch] Unable to load the barcode scanner.', error); // eslint-disable-line no-console
                this.loadingContainer.classList.add('hidden');
                this.notFoundContainer.textContent = 'Der Barcode-Scanner konnte nicht geladen werden. Bitte versuchen Sie es erneut.';
                this.notFoundContainer.classList.remove('hidden');
                return;
            }

            // The drawer may have been closed while the bundle was downloading.
            if (!this.isConnected) return;

            Quagga.init(this.config, this.quaggaInit.bind(this));
        }

        async handleGoOnline() {
            this.online = true;
            const offlineAddToCart = JSON.parse(localStorage.getItem('offline_cart_add') || '[]');
            if (offlineAddToCart.length) {

                const addItems = [];

                // {% comment %}theme-check-disable{% endcomment %}
                for await (const { handle, quantity, variantId } of offlineAddToCart) {

                    const priceRequest = await fetch(`/products/${handle}?variant=${variantId}&view=json`);
                    const { selected_variant } = await priceRequest.json();

                    addItems.push({
                        id: item.variantId,
                        quantity: item.quantity,
                        properties: {
                            '_price': selected_variant.price
                        }
                    });
                }

                await fetch(`${window.theme.routes.cartAdd}.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        items: [addItems]
                    })
                });


                // Dispatch cart added event
                document.dispatchEvent(new CustomEvent('cart:item:added', {
                    detail: { timestamp: Date.now(), source: 'barcode-items' }
                }));

                // {% comment %}theme-check-enable{% endcomment %}
                localStorage.removeItem('offline_cart_add');
            }
        }

        handleGoOffline() {
            this.online = false;
        }

        async initVideoDevices() {
            this.videoDevices = await Quagga.CameraAccess.enumerateVideoDevices();
            const hasMultipleDevices = this.videoDevices.length > 1;
            if (hasMultipleDevices) {
                const deviceOptions = this.videoDevices.map(device => {
                    const selected = this.config.inputStream.constraints.deviceId != null && this.config.inputStream.constraints.deviceId == device.deviceId;
                    return `<option value="${device.deviceId}" ${selected ? 'selected' : ''}>${device.label}</option>`;
                }).join('');
                this.deviceSelectionContainer.querySelector('select').innerHTML = deviceOptions;
                this.deviceSelectionContainer.classList.remove('hidden');
            }
        }

        handleChangeDevice(evt) {
            // The select is live before the bundle finishes downloading.
            if (!window.Quagga) return;

            this.resetSearch();
            Quagga.stop();
            this.config.inputStream.constraints.deviceId = evt.target.value;
            Quagga.init(this.config, this.quaggaInit.bind(this));
        }

        async preload() {
            return new Promise(async (resolve) => {
                let finished = false;
                let cursor = null;
                do {
                    const query = {
                        "query": `{
                            products(first: 250${cursor != null ? `, after: "${cursor}"` : ''}) {
                                edges {
                                cursor
                                node {
                                    id
                                    title
                                    handle
                                    metafield(key: "product_identifier", namespace: "stroetmann") {
                                    value
                                    }
                                    variants(first: 100) {
                                    edges {
                                        node {
                                        id
                                        title
                                        barcode
                                        }
                                    }
                                    }
                                }
                                }
                            }
                            }`
                    }
                    const response = await fetch('/api/2021-07/graphql.json', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Shopify-Storefront-Access-Token': this.token
                        },
                        body: JSON.stringify(query)
                    });
                    if (response.ok) {
                        const { data } = await response.json();
                        // console.log(data);
                        if (data.products.edges.length && data.products.edges[data.products.edges.length - 1].cursor) {
                            for (let index = 0; index < data.products.edges.length; index++) {
                                const { node } = data.products.edges[index];
                                for (let variantIndex = 0; variantIndex < node.variants.edges.length; variantIndex++) {
                                    const variantEdge = node.variants.edges[variantIndex];
                                    this.products.push({
                                        handle: node.handle,
                                        title: node.title,
                                        variantId: variantEdge.node.id.replace('gid://shopify/ProductVariant/', ''),
                                        variantTitle: variantEdge.node.title,
                                        barcode: variantEdge.node.barcode,
                                        // sid: node.metafield.value
                                    });
                                }
                            }
                            cursor = data.products.edges[data.products.edges.length - 1].cursor;
                        } else {
                            finished = true;
                        }
                    } else {
                        finished = true;
                    }
                } while (!finished);

                const date = new Date();
                date.setDate(date.getDate() + 1);

                localStorage.setItem('barcode_offline_products', JSON.stringify(this.products));
                localStorage.setItem('barcode_offline_timestamp', date);

                resolve();
            });
        }

        quaggaInit(err) {
            if (err) {
                console.log(err);
                return;
            }
            this.loadingContainer.classList.add('hidden');
            Quagga.onDetected(this.onDetected.bind(this));
            Quagga.start();
            document.addEventListener('keydown', this.escapeEventListener.bind(this));
            this.initVideoDevices();
        }

        escapeEventListener(evt) {
            if (evt.key !== 'Escape') return;
            document.dispatchEvent(new CustomEvent('barcode:search:close'), { bubbles: true });
        }

        onDetected(data) {
            const { codeResult } = data;
            const { code } = codeResult;
            if (this.code != code && code != null) {
                this.resetSearch();
                this.code = code;
            }
        }

        resetSearch() {
            this.resultContainer.innerHTML = '';
            this.notFoundContainer.classList.add('hidden');
            this.notFoundContainer.innerHTML = '';
            this.code = null;
        }

        async search() {

            this.resultContainer.innerHTML = '';
            this.loadingResultContainer.classList.remove('hidden');

            if (this.online) {
                this.onlineSearch();
            } else {
                this.offlineSearch();
            }

        }

        async offlineSearch() {

            const offlineProduct = this.products.find((product) => product.barcode == this.code);

            if (!offlineProduct) {
                this.loadingResultContainer.classList.add('hidden');
                this.notFoundContainer.innerHTML = window.theme.strings.barcode.unknown_ean.replace('##EAN##', this.code);
                this.notFoundContainer.classList.remove('hidden');
                // setTimeout(this.resetSearch.bind(this), 4000);
                return;
            }

            this.loadingResultContainer.classList.add('hidden');

            this.resultContainer.innerHTML = `
            <div class="main-products-grid main-products-grid--contained flex" data-layout="list">
                <div class="main-products-grid__results relative flex-auto" id="psearch-product" role="tabpanel" tabindex="0"
                    aria-labelledby="psearch-product-tab">
                    <ul class="grid gapless mb-8 md:mb-12 grid-cols-2 small-cards-mobile sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 small-cards-desktop gap-card"
                        role="list">
                        <li class="promo-item--grid_list js-pagination-result" role="option">
                            <div id="shopify-section-product-card" class="shopify-section">
                                <product-card
                                    class="card card--product h-full card--product-contained card--product-contained-outset card--no-lines relative flex"
                                    data-product-handle="schwarzer-tee-kenia-25-beutel-unkuvertiert-goldmnnchen">
                                    <div class="card__media relative">
                                        <a href="/products/schwarzer-tee-kenia-25-beutel-unkuvertiert-goldmnnchen"
                                            aria-label="Schwarzer Tee Kenia 25 Beutel unkuvertiert Goldmnnchen"
                                            class="media block relative js-prod-link" style="padding-top: 100%;" tabindex="-1">
                                            <svg class="media__placeholder img-fit" xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 525.5 525.5">
                                                <path
                                                    d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z">
                                                </path>
                                                <path
                                                    d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z">
                                                </path>
                                                <path
                                                    d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z">
                                                </path>
                                            </svg>
                                        </a>
                                    </div>
                                    <div class="card__info-container flex flex-col flex-auto relative">
                                        <div class="card__info w-full">
                                            <div class="card__info-inner flex flex-col h-full text-center w-full">
                                                <p class="card__title font-bold mt-1 mb-0">
                                                    ${offlineProduct.title}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="card__quick-add mob:card__quick-add--below desktop:card__quick-add--below">
                                            <div class="flex flex-row gap-3">
                                                <div class="block relative text-center">
                                                    <div class="flex flex-col gap-3 justify-stretch">
                                                        <div class="js-prod-card-variant-select">
                                                            <div class="custom-select relative w-full no-js-hidden">
                                                                <button class="custom-select__btn input input--sm items-center"
                                                                    type="button" aria-expanded="false" aria-haspopup="listbox"
                                                                    id="-button" style="--custom-select-button-width: -4px;">
                                                                    <span class="text-start">${offlineProduct.variantTitle}</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <quantity-adjust-offline class="inline-block js-prod-card-quantity-adjust"
                                                            data-product-handle="${offlineProduct.handle}"
                                                            data-variant-id="${offlineProduct.variantId}">
                                                            <label class="label visually-hidden" for="">Anzahl</label>
                                                            <div
                                                                class="qty-input qty-input--combined inline-flex items-center w-full">
                                                                <button type="button"
                                                                    class="qty-input__btn btn btn--minus no-js-hidden js-quantity-adjust-decrease"
                                                                    name="minus">
                                                                    <span class="visually-hidden">-</span>
                                                                </button>
                                                                <input type="number"
                                                                    class="qty-input__input input js-quantity-adjust-input"
                                                                    aria-label="Anzahl" name="quantity" min="0" value="0">
                                                                <button type="button"
                                                                    class="qty-input__btn btn btn--plus no-js-hidden js-quantity-adjust-increase"
                                                                    name="plus">
                                                                    <span class="visually-hidden">+</span>
                                                                </button>
                                                            </div>
                                                        </quantity-adjust-offline>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </product-card>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>`;
        }

        async onlineSearch() {

            const query = {
                "query": `{ products(first: 1, query: \"${this.code}\") { edges { node { handle } } } }`
            }

            const response = await fetch('/api/2021-07/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': this.token
                },
                body: JSON.stringify(query)
            });

            const { data } = await response.json();
            const { products } = data;

            const productFound = products.edges.length > 0;

            if (!productFound) {
                this.loadingResultContainer.classList.add('hidden');
                this.notFoundContainer.innerHTML = window.theme.strings.barcode.unknown_ean.replace('##EAN##', this.code);
                this.notFoundContainer.classList.remove('hidden');
                // setTimeout(this.resetSearch.bind(this), 4000);
                return;
            }

            const { handle } = products.edges[0].node;

            const sectionResponse = await fetch(`/products/${handle}?section_id=product-card`);
            const sectionText = await sectionResponse.text();

            this.loadingResultContainer.classList.add('hidden');

            if (!this.tempResults.includes(sectionText)) {
                this.tempResults.push(sectionText);
            }

            localStorage.setItem('barcode_cached_results', JSON.stringify(this.tempResults))

            this.resultContainer.innerHTML = `
                <div class="main-products-grid main-products-grid--contained flex" data-layout="list">
                    <div
                        class="main-products-grid__results relative flex-auto"
                        id="psearch-product"
                        role="tabpanel"
                        tabindex="0"
                        aria-labelledby="psearch-product-tab"
                    >
                        <ul class="grid gapless mb-8 md:mb-12 grid-cols-2 small-cards-mobile sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 small-cards-desktop gap-card" role="list">
                            <li class="promo-item--grid_list js-pagination-result" role="option">${sectionText}</li>
                        </ul>
                    </div>
                </div>`;

            this.cacheContainer.innerHTML = `
                <div class="main-products-grid main-products-grid--contained flex" data-layout="list">
                    <div
                        class="main-products-grid__results relative flex-auto"
                        id="psearch-product"
                        role="tabpanel"
                        tabindex="0"
                        aria-labelledby="psearch-product-tab"
                    >
                        <div class="promo-item-scan-title">Vorherige Scanergebnisse:</div>
                        <ul class="grid gapless mb-8 md:mb-12 grid-cols-2 small-cards-mobile sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 small-cards-desktop gap-card" role="list">
                            <li class="promo-item--grid_list js-pagination-result" role="option">${this.tempResults.join('')}</li>
                        </ul>
                    </div>
                </div>`;
            // setTimeout(this.resetSearch.bind(this), 4000);
        }
    }

    customElements.define('barcode-search', BarcodeSearch);
}

if (!customElements.get('barcode-search-button')) {
    class BarcodeSearchButton extends HTMLElement {
        constructor() {
            super();

            this.overlay = document.querySelector('.js-barcode-overlay');
            this.content = document.querySelector('[data-barcode-search]');
            this.predictiveSearchContainer = document.querySelector('predictive-search');
        }

        disconnectedCallback() {
            this.close();
        }

        connectedCallback() {
            document.addEventListener('barcode:search:close', this.close.bind(this));
            this.addEventListener('click', () => {
                const markup = `<div class="barcode-search absolute bg-theme-bg text-theme-text text-start">
                    <div class="barcode-search__scroller overflow-auto">
                        <div class="predictive-search__results flex flex-col">
                            <barcode-search class="flex flex-col mt-1" style="align-items: center;" data-token="${this.dataset.token}">
                                <div class="barcode-loading" data-loading>
                                    <div class="loader"></div>
                                    ${window.theme.strings.barcode.loading}
                                </div>
                                <div class="hidden mt-2 mb-2" data-device-selection-container>
                                    <label for="device-selection">${window.theme.strings.barcode.device}</label>
                                    <select id="device-selection"></select>
                                </div>
                                <div id="barCodeScanner"></div>
                                <h3 class="barcode-info">${window.theme.strings.barcode.use_barcode}</h3>
                                    <div class="hidden result-loading" data-loading-result>
                                    <div class="loader"></div>
                                    ${window.theme.strings.barcode.loading}
                                </div>
                                <div style="width: 100%;" data-barcode-result></div>
                                <div style="width: 100%;" data-barcode-result-cached></div>
                                <div class="text-center alert mb-8 bg-error-bg text-error-text hidden" data-not-found>${window.theme.strings.barcode.unknown_ean}</div>
                            </barcode-search>
                        </div>
                    </div>
                </div>`;
                this.content.innerHTML = markup;
                this.open();
            });
        }

        open() {
            document.dispatchEvent(new CustomEvent('predictive:search:close'), { bubbles: true });
            this.overlay.classList.add('is-visible');
            this.predictiveSearchContainer.setAttribute('open-barcode', '');
            document.body.classList.add('overlay-predictive-search');
            this.overlay.addEventListener('click', this.close.bind(this));
        }

        close() {
            this.predictiveSearchContainer.removeAttribute('open-barcode');
            this.overlay.classList.remove('is-visible');
            document.body.classList.remove('overlay-predictive-search');
            this.content.innerHTML = '';
            this.overlay.removeEventListener('click', this.close.bind(this));
        }
    }

    customElements.define('barcode-search-button', BarcodeSearchButton);
}

if (!customElements.get('quantity-adjust-offline')) {
    class QuantityAdjustOffline extends HTMLElement {
        constructor() {
            super();
            this.elems = this.getElems();
            this.handle = this.dataset.productHandle;
            this.variantId = this.dataset.variantId;
            this._quantity = 0;
        }

        set quantity(value) {
            let intValue = parseInt(value);
            if (intValue <= 0) {
                this._quantity = 0;
            } else {
                this._quantity = intValue;
            }
            this.elems.input.value = this._quantity;
            this.handleStorage();
        }

        get quantity() {
            return this._quantity;
        }

        disconnectedCallback() {
            this.elems.input.removeEventListener('blur', this.handleInputChange.bind(this));
            this.elems.decrease.removeEventListener('click', this.handleDecrease.bind(this));
            this.elems.increase.removeEventListener('click', this.handleIncrease.bind(this));
        }

        connectedCallback() {
            this.elems.input.addEventListener('blur', this.handleInputChange.bind(this));
            this.elems.decrease.addEventListener('click', this.handleDecrease.bind(this));
            this.elems.increase.addEventListener('click', this.handleIncrease.bind(this));

            const offlineCartAdd = JSON.parse(localStorage.getItem('offline_cart_add') || '[]').find(element => element.variantId == this.variantId);
            // if (offlineCartAdd) {
            //     console.log(offlineCartAdd);
            // }
        }

        handleStorage() {
            const offlineCartAdd = JSON.parse(localStorage.getItem('offline_cart_add') || '[]').filter(element => element.variantId != this.variantId);
            if (this.quantity > 0) {
                offlineCartAdd.push({
                    quantity: this.quantity,
                    handle: this.handle,
                    variantId: this.variantId,
                });
            }
            localStorage.setItem('offline_cart_add', JSON.stringify(offlineCartAdd));
        }

        handleInputChange(e) {
            this.quantity = e.target.value;
        }

        handleDecrease() {
            this.quantity = this.quantity - 1;
        }

        handleIncrease() {
            this.quantity = this.quantity + 1;
        }

        getElems() {
            return {
                decrease: this.querySelector('.js-quantity-adjust-decrease'),
                increase: this.querySelector('.js-quantity-adjust-increase'),
                input: this.querySelector('.js-quantity-adjust-input'),
            }
        }
    }

    customElements.define('quantity-adjust-offline', QuantityAdjustOffline);
}